import { useState, useRef, useCallback, useEffect } from 'react';
import type { YoutubeSlideHandle } from '../components/YoutubeSlide';
import type { CaptureSlideHandle, WindowCaptureHandle } from '../components/CaptureSlide';
import type { PlaylistItem, FilaJukebox, Tela } from '@/shared/types';
import { resolverFaixaAtual, montarMusicaAtualBroadcast } from '../../domain/usecases/faixaAtual';
import { volumeNaPlaylist } from '../../domain/usecases/volumeDuranteLocucao';

export interface UsePlayerRemoteStateParams {
  tela: Tela | null;
  sendToTela: (event: string, payload: Record<string, unknown>) => void;
  ytSlideRef:      React.RefObject<YoutubeSlideHandle | null>;
  ytLiveRef:       React.RefObject<YoutubeSlideHandle | null>;
  captureSlideRef: React.RefObject<CaptureSlideHandle | null>;
  windowCaptureRef: React.RefObject<WindowCaptureHandle | null>;
  videoRef:        React.RefObject<HTMLVideoElement | null>;
  volumePadrao:        number;
  qrcodeJukebox:       boolean;
  reproducaoAleatoria: boolean;
  pedidoAtualRef:      React.RefObject<FilaJukebox | null>;
  playlistItemsRef:    React.RefObject<PlaylistItem[]>;
  ytIndexRef:          React.RefObject<number>;
  // Refs compartilhadas criadas em Player.tsx e passadas para cá (em vez de
  // criadas internamente) para quebrar a dependência circular com
  // usePlayerMediaSource/useMomentoEspecial, que também as consomem e são
  // declarados antes deste hook no corpo de Player().
  playerPlayingRef:         React.RefObject<boolean>;
  playerMutedRef:           React.RefObject<boolean>;
  playerVolumeRef:          React.RefObject<number>;
  broadcastEstadoPlayerRef: React.RefObject<() => void>;
  /** true enquanto a locução da Identidade Sonora está no ar. */
  locucaoAtivaRef:          React.RefObject<boolean>;
}

export interface UsePlayerRemoteStateReturn {
  showNetworkHud: boolean;
  setShowNetworkHud: React.Dispatch<React.SetStateAction<boolean>>;
  qrVisible: boolean;
  setQrVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setIsRandom: React.Dispatch<React.SetStateAction<boolean>>;
  flashMessage: { text: string; style: 'modal' | 'ticker'; duration: number } | null;
  setFlashMessage: React.Dispatch<React.SetStateAction<{ text: string; style: 'modal' | 'ticker'; duration: number } | null>>;
  playerPlayingRef: React.RefObject<boolean>;
  playerMutedRef:   React.RefObject<boolean>;
  playerVolumeRef:  React.RefObject<number>;
  broadcastEstadoPlayerRef: React.RefObject<() => void>;
  musicaIniciadaEmRef:   React.RefObject<string | null>;
  musicaDuracaoAtualRef: React.RefObject<number | null>;
  togglePlay(): void;
  toggleMute(): void;
  setVolume(volume: number): void;
  broadcastMusicaAtual(item: PlaylistItem | FilaJukebox): void;
  broadcastEstadoPlayer(): void;
}

/** Estado de execução do Controle Remoto (play/pause/mute/volume/QR/flash),
 *  e os broadcasts `musica_atual`/`estado_player` enviados à TV. */
export function usePlayerRemoteState({
  tela: _tela, sendToTela, ytSlideRef, ytLiveRef, captureSlideRef, windowCaptureRef, videoRef, volumePadrao,
  qrcodeJukebox, reproducaoAleatoria, pedidoAtualRef, playlistItemsRef, ytIndexRef,
  playerPlayingRef, playerMutedRef, playerVolumeRef, broadcastEstadoPlayerRef,
  locucaoAtivaRef,
}: UsePlayerRemoteStateParams): UsePlayerRemoteStateReturn {
  const [showNetworkHud, setShowNetworkHud] = useState(false);
  const [qrVisible, setQrVisible] = useState(true);
  const [, setIsRandom] = useState(false); // pendente: lógica de shuffle não implementada
  const [flashMessage, setFlashMessage] = useState<{ text: string; style: 'modal' | 'ticker'; duration: number } | null>(null);

  // Rastreia quando a música atual começou e sua duração — necessário para
  // responder a solicitar_musica_atual com dados corretos de progresso.
  const musicaIniciadaEmRef   = useRef<string | null>(null);
  const musicaDuracaoAtualRef = useRef<number | null>(null);

  useEffect(() => { setQrVisible(qrcodeJukebox); }, [qrcodeJukebox]);
  useEffect(() => { setIsRandom(reproducaoAleatoria); }, [reproducaoAleatoria]);

  useEffect(() => {
    playerVolumeRef.current = volumePadrao;
    ytSlideRef.current?.setVolume(volumePadrao);
    ytLiveRef.current?.setVolume(volumePadrao);
    captureSlideRef.current?.setVolume(volumePadrao);
    windowCaptureRef.current?.setVolume(volumePadrao);
    if (videoRef.current) videoRef.current.volume = volumePadrao / 100;
  }, [volumePadrao, ytSlideRef, ytLiveRef, videoRef, captureSlideRef, windowCaptureRef, playerVolumeRef]);

  // A captura de janela pode montar depois da configuração inicial. Usa o
  // volume atual para não sobrescrever ajustes recebidos do controle remoto.
  useEffect(() => {
    windowCaptureRef.current?.setVolume(playerMutedRef.current ? 0 : playerVolumeRef.current);
  });

  const broadcastMusicaAtual = useCallback((item: PlaylistItem | FilaJukebox) => {
    const agora = new Date().toISOString();
    const faixa = 'youtube_video_id' in item
      ? resolverFaixaAtual(item, undefined)
      : resolverFaixaAtual(undefined, item);
    const duracao = item.duracao_segundos ?? null;
    musicaIniciadaEmRef.current = agora;
    musicaDuracaoAtualRef.current = duracao;
    const musicaAtual = montarMusicaAtualBroadcast(faixa, {
      duracao_segundos: duracao,
      iniciada_em: agora,
    });
    if (!musicaAtual) return;
    sendToTela('musica_atual', musicaAtual as unknown as Record<string, unknown>);
  }, [sendToTela]);

  const broadcastEstadoPlayer = useCallback(() => {
    const faixa = resolverFaixaAtual(pedidoAtualRef.current, playlistItemsRef.current[ytIndexRef.current]);
    const musicaAtual = montarMusicaAtualBroadcast(faixa, { duracao_segundos: null });
    sendToTela('estado_player', {
      isPlaying:   playerPlayingRef.current,
      isMuted:     playerMutedRef.current,
      volume:      playerVolumeRef.current,
      musicaAtual: musicaAtual as unknown as Record<string, unknown>,
    });
  }, [sendToTela, pedidoAtualRef, playlistItemsRef, ytIndexRef, playerPlayingRef, playerMutedRef, playerVolumeRef]);

  useEffect(() => { broadcastEstadoPlayerRef.current = broadcastEstadoPlayer; }, [broadcastEstadoPlayer, broadcastEstadoPlayerRef]);

  const togglePlay = useCallback(() => {
    const playing = playerPlayingRef.current;
    if (playing) {
      ytSlideRef.current?.pause();
      ytLiveRef.current?.pause();
    } else {
      ytSlideRef.current?.play();
      ytLiveRef.current?.play();
    }
    playerPlayingRef.current = !playing;
    broadcastEstadoPlayer();
  }, [ytSlideRef, ytLiveRef, broadcastEstadoPlayer, playerPlayingRef]);

  const toggleMute = useCallback(() => {
    const muted = playerMutedRef.current;
    if (muted) {
      ytSlideRef.current?.unMute();
      ytLiveRef.current?.unMute();
    } else {
      ytSlideRef.current?.mute();
      ytLiveRef.current?.mute();
    }
    captureSlideRef.current?.setVolume(muted ? playerVolumeRef.current : 0);
    windowCaptureRef.current?.setVolume(muted ? playerVolumeRef.current : 0);
    playerMutedRef.current = !muted;
    broadcastEstadoPlayer();
  }, [ytSlideRef, ytLiveRef, captureSlideRef, windowCaptureRef, broadcastEstadoPlayer, playerMutedRef, playerVolumeRef]);

  const setVolume = useCallback((volume: number) => {
    playerVolumeRef.current = volume;
    const naPlaylist = volumeNaPlaylist(volume, locucaoAtivaRef.current);
    if (naPlaylist !== null) ytSlideRef.current?.setVolume(naPlaylist);
    ytLiveRef.current?.setVolume(volume);
    if (videoRef.current) videoRef.current.volume = volume / 100;
    captureSlideRef.current?.setVolume(volume);
    windowCaptureRef.current?.setVolume(volume);

    if (volume > 0 && playerMutedRef.current) {
      ytSlideRef.current?.unMute();
      ytLiveRef.current?.unMute();
      playerMutedRef.current = false;
      broadcastEstadoPlayer();
    }
  }, [ytSlideRef, ytLiveRef, videoRef, captureSlideRef, windowCaptureRef, broadcastEstadoPlayer, playerMutedRef, playerVolumeRef, locucaoAtivaRef]);

  return {
    showNetworkHud, setShowNetworkHud,
    qrVisible, setQrVisible,
    setIsRandom,
    flashMessage, setFlashMessage,
    playerPlayingRef, playerMutedRef, playerVolumeRef, broadcastEstadoPlayerRef,
    musicaIniciadaEmRef, musicaDuracaoAtualRef,
    togglePlay, toggleMute, setVolume,
    broadcastMusicaAtual, broadcastEstadoPlayer,
  };
}
